import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsProcedures, useAgents, useDomainesProcedures, useGestionProcedures, useProcedure, useStructures } from '@/api/hooks';
import { CATEGORIES_AGENT, LIBELLE_CATEGORIE_AGENT, LIBELLE_PROFIL, LIBELLE_TYPE_CIBLE_PROCEDURE, TYPES_CIBLE, type CibleSaisie, type DetailProcedure, type Profil, type TypeCible } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { dateFr } from '@/lib/utils';
import { StatutProcedureBadge } from './ProcedureFiche';

type Onglet = 'mes' | 'a-valider' | 'toutes';
const message = (e: unknown) => (e instanceof Error ? e.message : 'Action impossible.');
const PROFILS = Object.keys(LIBELLE_PROFIL) as Profil[];

interface Formulaire {
  reference: string;
  titre: string;
  domaineId: string;
  description: string;
  structureResponsableId: string;
  responsableId: string;
  motsCles: string;
  confirmationRequise: boolean;
  telechargementAutorise: boolean;
  impressionAutorisee: boolean;
  visaDg: boolean;
  cibles: CibleSaisie[];
}

const VIDE: Formulaire = { reference: '', titre: '', domaineId: '', description: '', structureResponsableId: '', responsableId: '', motsCles: '', confirmationRequise: false, telechargementAutorise: true, impressionAutorisee: true, visaDg: false, cibles: [{ type: 'TOUS' }] };

/** Éditeur des cibles (§ 6.1) : tous les agents, structures, catégories de fonction, profils, agents nommés. */
function CiblesEditeur({ cibles, onChange }: { cibles: CibleSaisie[]; onChange: (c: CibleSaisie[]) => void }) {
  const structures = useStructures();
  const agents = useAgents();
  const maj = (i: number, c: CibleSaisie) => onChange(cibles.map((x, k) => (k === i ? c : x)));
  return (
    <div className="space-y-2">
      {cibles.map((c, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <SelectNatif value={c.type} onChange={(e) => maj(i, { type: e.target.value as TypeCible, valeur: null })} className="sm:w-56">
            {TYPES_CIBLE.map((t) => (
              <option key={t} value={t}>
                {LIBELLE_TYPE_CIBLE_PROCEDURE[t]}
              </option>
            ))}
          </SelectNatif>
          <div className="min-w-56 flex-1">
            {c.type === 'STRUCTURE' && (
              <SelectNatif value={c.valeur ?? ''} onChange={(e) => maj(i, { ...c, valeur: e.target.value })}>
                <option value="">Choisir une structure</option>
                {(structures.data ?? []).map((s) => (
                  <option key={s.id} value={s.code}>
                    {s.code} · {s.libelle}
                  </option>
                ))}
              </SelectNatif>
            )}
            {c.type === 'FONCTION' && (
              <SelectNatif value={c.valeur ?? ''} onChange={(e) => maj(i, { ...c, valeur: e.target.value })}>
                <option value="">Choisir une catégorie</option>
                {CATEGORIES_AGENT.map((k) => (
                  <option key={k} value={k}>
                    {LIBELLE_CATEGORIE_AGENT[k]}
                  </option>
                ))}
              </SelectNatif>
            )}
            {c.type === 'PROFIL' && (
              <SelectNatif value={c.valeur ?? ''} onChange={(e) => maj(i, { ...c, valeur: e.target.value })}>
                <option value="">Choisir un profil</option>
                {PROFILS.map((k) => (
                  <option key={k} value={k}>
                    {LIBELLE_PROFIL[k]}
                  </option>
                ))}
              </SelectNatif>
            )}
            {c.type === 'AGENT' && (
              <SelectNatif value={c.valeur ?? ''} onChange={(e) => maj(i, { ...c, valeur: e.target.value })}>
                <option value="">Choisir un agent</option>
                {(agents.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.prenom} {a.nom} · {a.matricule}
                  </option>
                ))}
              </SelectNatif>
            )}
            {c.type === 'TOUS' && <span className="text-sm text-muted-foreground">Tous les agents actifs du Fonds.</span>}
          </div>
          <Button type="button" variant="ghost" size="icon" aria-label="Retirer la cible" onClick={() => onChange(cibles.filter((_, k) => k !== i))} disabled={cibles.length === 1}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...cibles, { type: 'STRUCTURE', valeur: null }])}>
        <Plus className="size-4" /> Ajouter une cible
      </Button>
    </div>
  );
}

/** Création ou modification de la fiche d'une procédure (§ 6.1, 6.5). */
function FormulaireProcedure({ ouvert, initial, onClose, onEnregistre }: { ouvert: boolean; initial: DetailProcedure | null; onClose: () => void; onEnregistre: (d: DetailProcedure) => void }) {
  const actions = useActionsProcedures();
  const domaines = useDomainesProcedures();
  const structures = useStructures();
  const [f, setF] = useState<Formulaire>(VIDE);
  const structureCode = (structures.data ?? []).find((s) => s.id === f.structureResponsableId)?.code;
  const agents = useAgents(structureCode);
  useEffect(() => {
    if (!ouvert) return;
    if (!initial) {
      setF(VIDE);
      return;
    }
    const p = initial.procedure;
    setF({ reference: p.reference, titre: p.titre, domaineId: p.domaine.id, description: p.description ?? '', structureResponsableId: p.structureResponsable.id, responsableId: p.responsable.id, motsCles: p.motsCles.join(', '), confirmationRequise: p.confirmationRequise, telechargementAutorise: p.telechargementAutorise, impressionAutorisee: p.impressionAutorisee, visaDg: p.visaDg, cibles: initial.cibles.map((c) => ({ type: c.type, valeur: c.valeur })) });
  }, [ouvert, initial]);
  const champ = <K extends keyof Formulaire>(k: K, v: Formulaire[K]) => setF((x) => ({ ...x, [k]: v }));
  const enregistrer = async () => {
    const dto = { titre: f.titre.trim(), domaineId: f.domaineId, description: f.description.trim() || null, structureResponsableId: f.structureResponsableId, responsableId: f.responsableId, motsCles: f.motsCles.split(/[,;]/).map((m) => m.trim()).filter(Boolean), confirmationRequise: f.confirmationRequise, telechargementAutorise: f.telechargementAutorise, impressionAutorisee: f.impressionAutorisee, visaDg: f.visaDg, cibles: f.cibles.map((c) => ({ type: c.type, valeur: c.type === 'TOUS' ? null : c.valeur })) };
    try {
      const d = initial ? await actions.modifier.mutateAsync({ id: initial.procedure.id, ...dto }) : await actions.creer.mutateAsync({ reference: f.reference.trim(), ...dto });
      toast.success(initial ? 'Fiche enregistrée.' : 'Procédure créée en brouillon : joignez le document PDF puis soumettez la version.');
      onEnregistre(d);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? `Modifier ${initial.procedure.reference}` : 'Nouvelle procédure'}</DialogTitle>
          <DialogDescription>Fiche, responsable métier, cibles et importance. Le document et les notes de version se joignent ensuite sur la fiche, puis la version est soumise au directeur de la structure responsable.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="p-reference">Référence (unique)</Label>
            <Input id="p-reference" value={f.reference} onChange={(e) => champ('reference', e.target.value)} placeholder="PRO-RH-003" disabled={Boolean(initial)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="p-domaine">Domaine</Label>
            <SelectNatif id="p-domaine" value={f.domaineId} onChange={(e) => champ('domaineId', e.target.value)}>
              <option value="">Choisir</option>
              {(domaines.data ?? [])
                .filter((d) => d.actif)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.libelle}
                  </option>
                ))}
            </SelectNatif>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="p-titre">Titre</Label>
            <Input id="p-titre" value={f.titre} onChange={(e) => champ('titre', e.target.value)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="p-description">Description (objet en quelques lignes)</Label>
            <Textarea id="p-description" rows={3} value={f.description} onChange={(e) => champ('description', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="p-structure">Structure responsable</Label>
            <SelectNatif id="p-structure" value={f.structureResponsableId} onChange={(e) => champ('structureResponsableId', e.target.value)}>
              <option value="">Choisir</option>
              {(structures.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="p-responsable">Agent responsable métier</Label>
            <SelectNatif id="p-responsable" value={f.responsableId} onChange={(e) => champ('responsableId', e.target.value)}>
              <option value="">Choisir</option>
              {(agents.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.prenom} {a.nom} · {a.fonction}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="p-mots">Mots-clés (séparés par des virgules)</Label>
            <Input id="p-mots" value={f.motsCles} onChange={(e) => champ('motsCles', e.target.value)} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Cibles</Label>
            <CiblesEditeur cibles={f.cibles} onChange={(c) => champ('cibles', c)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.confirmationRequise} onCheckedChange={(v) => champ('confirmationRequise', v === true)} /> Confirmation explicite requise
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.visaDg} onCheckedChange={(v) => champ('visaDg', v === true)} /> Visa du Directeur Général avant publication
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.telechargementAutorise} onCheckedChange={(v) => champ('telechargementAutorise', v === true)} /> Téléchargement autorisé
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.impressionAutorisee} onCheckedChange={(v) => champ('impressionAutorisee', v === true)} /> Impression autorisée
          </label>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="button" onClick={enregistrer} disabled={actions.creer.isPending || actions.modifier.isPending || !f.titre.trim() || !f.domaineId || !f.structureResponsableId || !f.responsableId || (!initial && !f.reference.trim())}>
            {initial ? 'Enregistrer' : 'Créer la procédure'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Gestion des procédures (§ 6.8) : mes procédures (responsable, RH pour le domaine RH), à valider, toutes ; création et modification de la fiche. */
export function GestionProcedures() {
  const [sp, setSp] = useSearchParams();
  const [onglet, setOnglet] = useState<Onglet>((sp.get('onglet') as Onglet | null) ?? 'mes');
  const { data, isLoading, error } = useGestionProcedures(onglet);
  const aModifier = sp.get('modifier');
  const detail = useProcedure(aModifier ?? undefined);
  const [nouvelle, setNouvelle] = useState(false);
  const fermer = () => {
    setNouvelle(false);
    const n = new URLSearchParams(sp);
    n.delete('modifier');
    setSp(n);
  };
  return (
    <div className="space-y-4">
      <PageHeader
        titre="Gestion des procédures"
        sousTitre="Création et modification réservées au responsable métier de chaque procédure (et aux RH pour le domaine RH) ; validation par le directeur de la structure responsable, visa du DG en option ; seules les procédures publiées sont diffusées."
        actions={
          <Button type="button" onClick={() => setNouvelle(true)}>
            <Plus className="size-4" /> Nouvelle procédure
          </Button>
        }
      />
      <Tabs value={onglet} onValueChange={(v) => setOnglet(v as Onglet)}>
        <TabsList>
          <TabsTrigger value="mes">Mes procédures</TabsTrigger>
          <TabsTrigger value="a-valider">À valider</TabsTrigger>
          <TabsTrigger value="toutes">Toutes</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card data-testid="gestion-procedures">
        <CardHeader>
          <CardTitle>{onglet === 'mes' ? 'Procédures dont je suis responsable' : onglet === 'a-valider' ? 'Versions soumises à ma validation' : 'Toutes les procédures'}</CardTitle>
          <CardDescription>Brouillon → En validation → Publiée → Archivée. Ouvrez la fiche pour joindre le document, soumettre, valider ou archiver.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Chargement />
          ) : error ? (
            <ErreurBloc erreur={error} />
          ) : !data?.length ? (
            <Vide message={onglet === 'a-valider' ? 'Aucune version en attente de votre validation.' : 'Aucune procédure.'} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Procédure</TableHead>
                  <TableHead>Domaine</TableHead>
                  <TableHead>Responsable</TableHead>
                  <TableHead>Cibles</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Version en vigueur</TableHead>
                  <TableHead>Mise à jour</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Link to={`/procedures/${p.id}`} className="font-medium text-primary hover:underline">
                        {p.reference}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={`/procedures/${p.id}`} className="hover:underline">
                        {p.titre}
                      </Link>
                      {p.confirmationRequise && (
                        <Badge variant="outline" className="ml-1">
                          confirmation
                        </Badge>
                      )}
                      {p.visaDg && (
                        <Badge variant="outline" className="ml-1">
                          visa DG
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{p.domaine.libelle}</TableCell>
                    <TableCell className="text-sm">
                      {p.structureResponsable.code}
                      <div className="text-xs text-muted-foreground">{p.responsable.nom}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.commune ? 'secondary' : 'outline'}>{p.etiquette}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatutProcedureBadge statut={p.statut} />
                    </TableCell>
                    <TableCell className="text-sm">{p.versionEnVigueur ? `v${p.versionEnVigueur.numero} · ${dateFr(p.versionEnVigueur.datePublication)}` : '—'}</TableCell>
                    <TableCell className="text-sm">{dateFr(p.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <FormulaireProcedure ouvert={nouvelle || (Boolean(aModifier) && Boolean(detail.data))} initial={aModifier ? (detail.data ?? null) : null} onClose={fermer} onEnregistre={fermer} />
    </div>
  );
}
